// Typy i metadane domeny CRM (współdzielone przez wszystkie widoki panelu).

export const STATUSES = ["new", "contacted", "quoted", "won", "lost"] as const;
export type LeadStatus = (typeof STATUSES)[number];

export const PRIORITIES = ["low", "normal", "high"] as const;
export type LeadPriority = (typeof PRIORITIES)[number];

export type AdminLead = {
  id: string;
  createdAt: string;
  updatedAt: string;
  source: string;
  name: string;
  phone: string;
  email: string;
  city: string;
  product: string;
  contactTime: string;
  message: string;
  contactConsent: boolean;
  privacyConsent: boolean;
  marketingConsent: boolean;
  selectedNeeds: string[];
  budget: number | null;
  age: string;
  family: string;
  recommendation: string;
  ipCountry: string;
  userAgent: string;
  legalVersion: string;
  contactConsentText: string;
  privacyConsentText: string;
  marketingConsentText: string;
  status: LeadStatus;
  owner: string;
  priority: LeadPriority;
};

export type LeadNote = {
  id: string;
  leadId: string;
  createdAt: string;
  author: string;
  body: string;
};

export type LeadTask = {
  id: string;
  leadId: string; // "" = zadanie samodzielne (bez powiązanego leada)
  createdAt: string;
  dueAt: string;
  title: string;
  owner: string;
  priority: LeadPriority;
  done: boolean;
  doneAt: string;
};

export type StatusMeta = { label: string; tone: string; description: string };

// Etapy lejka sprzedaży ubezpieczeń (kolejność = przepływ od lewej do prawej).
export const STATUS_ORDER: LeadStatus[] = ["new", "contacted", "quoted", "won", "lost"];

export const STATUS_META: Record<LeadStatus, StatusMeta> = {
  new: { label: "Nowy", tone: "blue", description: "Świeże zgłoszenie, czeka na pierwszy kontakt." },
  contacted: { label: "Kontakt", tone: "amber", description: "Rozpoczęta rozmowa, ustalanie potrzeb." },
  quoted: { label: "Oferta", tone: "violet", description: "Wysłana kalkulacja lub propozycja polisy." },
  won: { label: "Sprzedane", tone: "green", description: "Polisa zawarta — sukces sprzedaży." },
  lost: { label: "Rezygnacja", tone: "red", description: "Klient zrezygnował lub brak kontaktu." }
};

export const PRIORITY_META: Record<LeadPriority, { label: string; tone: string }> = {
  low: { label: "Niski", tone: "muted" },
  normal: { label: "Normalny", tone: "blue" },
  high: { label: "Wysoki", tone: "red" }
};

export const PRODUCT_ORDER = ["life", "endowment", "addons", "health", "mortgage", "wholelife"] as const;

export const PRODUCT_META: Record<string, { label: string; short: string }> = {
  life: { label: "Ubezpieczenie na życie (ochronne)", short: "Na życie" },
  endowment: { label: "Na życie i dożycie (oszczędnościowe)", short: "Życie i dożycie" },
  addons: { label: "Umowy dodatkowe do polisy", short: "Dodatki" },
  health: { label: "Pakiet zdrowotny / opieka medyczna", short: "Zdrowotne" },
  mortgage: { label: "Polisa na życie pod kredyt", short: "Pod kredyt" },
  wholelife: { label: "Dożywotnie na życie (Wsparcie Najbliższych)", short: "Dożywotnie" }
};

// Kolory wykresów (działają na jasnym i ciemnym tle).
export const PRODUCT_COLORS: Record<string, string> = {
  life: "#f26a21",
  endowment: "#2f80ed",
  addons: "#5f6f89",
  health: "#27ae60",
  mortgage: "#9b51e0",
  wholelife: "#f2994a"
};

export const STATUS_COLORS: Record<LeadStatus, string> = {
  new: "#2f80ed",
  contacted: "#f2994a",
  quoted: "#9b51e0",
  won: "#27ae60",
  lost: "#eb5757"
};

export type CrmView = "dashboard" | "pipeline" | "leads" | "tasks" | "analytics" | "chats";

export type AnalyticsSummary = {
  events: number;
  sessions: number;
  pageViews: number;
  calculatorResults: number;
  phoneClicks: number;
  formStarts: number;
  leads: number;
  paidSessions: number;
  paidLeads: number;
  leadConversion: number;
};

export type AnalyticsFunnelStep = {
  key: string;
  label: string;
  value: number;
};

export type AnalyticsDailyPoint = {
  date: string;
  label: string;
  events: number;
  sessions: number;
  leads: number;
};

export type AnalyticsLiveEvent = {
  id: string;
  createdAt: string;
  name: string;
  page: string;
  source: string;
  medium: string;
  campaign: string;
  gclid: string;
  gbraid: string;
  wbraid: string;
  utmContent: string;
  utmTerm: string;
  product: string;
  calculator: string;
  amount: number | null;
  device: string;
  viewportWidth: number | null;
  country: string;
  city: string;
  region: string;
  sessionTail: string;
};

export type AnalyticsReport = {
  ok: boolean;
  days: number;
  since: string;
  summary: AnalyticsSummary;
  funnel: AnalyticsFunnelStep[];
  daily: AnalyticsDailyPoint[];
  topPages: Array<{ page: string; views: number }>;
  calculators: Array<{ name: string; calculations: number; leads: number; conversion: number }>;
  sources: Array<{ source: string; sessions: number }>;
  devices: Array<{ device: string; sessions: number }>;
  liveEvents: AnalyticsLiveEvent[];
  paidCampaigns: Array<{
    campaign: string;
    clicks: number;
    leads: number;
    conversion: number;
  }>;
};

export type GoogleAdsCampaign = {
  id: string;
  name: string;
  cost: number;
  clicks: number;
  impressions: number;
  conversions: number;
  ctr: number;
  averageCpc: number;
  costPerConversion: number | null;
};

export type GoogleAdsReport = {
  ok: boolean;
  configured: boolean;
  days: number;
  currency: string;
  summary: {
    cost: number;
    clicks: number;
    impressions: number;
    conversions: number;
    ctr: number;
    averageCpc: number;
    costPerConversion: number | null;
  };
  campaigns: GoogleAdsCampaign[];
  error?: string;
};

export type ChatConversation = {
  id: string;
  createdAt: string;
  lastAt: string;
  ipCountry: string;
  userAgent: string;
  messageCount: number;
  firstQuestion: string;
};

export type ChatMessageRow = {
  id: string;
  role: "user" | "assistant";
  content: string;
  createdAt: string;
};
